import { Link, useForm } from '@inertiajs/react'
import { Award, UserPlus } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Button } from '@/components/ui/Button'
import { FieldError } from '@/components/feedback/FieldError'

type RegisterProps = {
  team: {
    id: string
    name: string
  }
  defaults?: {
    email: string
    password: string
  }
}

export default function PlayerRegister({ team, defaults }: RegisterProps) {
  const { data, setData, post, processing, errors, transform } = useForm({
    email: defaults?.email ?? '',
    password: defaults?.password ?? '',
    password_confirmation: defaults?.password ?? '',
  })

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    transform((current) => ({ player: current }))
    post(`/player/players?team_id=${team.id}`)
  }

  return (
    <AppShell>
      <div className="flex flex-col gap-10">
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-500/20 text-brand-100">
            <UserPlus className="h-6 w-6" />
          </div>
          <h1 className="mt-4 text-3xl font-semibold text-white">選手アカウント登録</h1>
          <p className="mt-2 text-sm text-white/60">
            {team.name} に所属する選手としてTalentboxへ参加します。登録後にプロフィール情報を入力してください。
          </p>
        </div>

        <form onSubmit={submit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">メールアドレス</Label>
              <Input
                id="email"
                type="email"
                value={data.email}
                onChange={(event) => setData('email', event.target.value)}
                autoComplete="email"
                placeholder="player@example.com"
                required
              />
              <FieldError message={errors.email} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">パスワード</Label>
              <Input
                id="password"
                type="password"
                value={data.password}
                onChange={(event) => setData('password', event.target.value)}
                autoComplete="new-password"
                placeholder="8文字以上"
                required
              />
              <FieldError message={errors.password} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="passwordConfirmation">パスワード（確認）</Label>
              <Input
                id="passwordConfirmation"
                type="password"
                value={data.password_confirmation}
                onChange={(event) =>
                  setData('password_confirmation', event.target.value)
                }
                autoComplete="new-password"
                placeholder="再度入力"
                required
              />
              <FieldError message={errors.password_confirmation} />
            </div>
          </div>

          <Button type="submit" loading={processing} className="w-full justify-center">
            <Award className="mr-2 h-4 w-4" /> 登録してプロフィール入力へ
          </Button>
        </form>

        <p className="text-center text-sm text-white/70">
          すでにアカウントをお持ちの方は{' '}
          <Link
            href="/player/login"
            className="font-medium text-brand-300 underline-offset-4 hover:underline"
          >
            ログイン
          </Link>
          へお進みください。
        </p>

        <p className="text-center text-xs text-white/40">
          登録することで利用規約およびプライバシーポリシーに同意したものとみなされます。
        </p>
      </div>
    </AppShell>
  )
}
